import type { Catalog, FlatMessages, Locale, Messages, Namespace } from './types'

// De catalogi worden statisch geïmporteerd. Dat is bewust: een statische import
// is voor iedere consument analyseerbaar — Next.js (server én client), Vite en
// tsx — terwijl een dynamische import per bundler ander gedrag geeft en in
// server components een extra await afdwingt.
import enCommon from '../locales/en/common.json' with { type: 'json' }
import enGuest from '../locales/en/guest.json' with { type: 'json' }
import enDashboard from '../locales/en/dashboard.json' with { type: 'json' }
import enAdmin from '../locales/en/admin.json'
import enAmenities from '../locales/en/amenities.json' with { type: 'json' }
import nlAmenities from '../locales/nl/amenities.json' with { type: 'json' }
import nlCommon from '../locales/nl/common.json' with { type: 'json' }
import nlGuest from '../locales/nl/guest.json' with { type: 'json' }
import nlDashboard from '../locales/nl/dashboard.json' with { type: 'json' }

/**
 * Register van alle aanwezige catalogi.
 *
 * `nl` heeft géén `admin`: de administrator backend wordt niet vertaald (§17.8).
 * Dat is een bewuste leemte, geen omissie, en `scripts/check-keys.ts` weet dat.
 */
const CATALOGS: Readonly<Record<Locale, Readonly<Partial<Record<Namespace, Catalog>>>>> = {
  en: {
    common: enCommon,
    guest: enGuest,
    dashboard: enDashboard,
    admin: enAdmin,
    amenities: enAmenities,
  },
  nl: {
    common: nlCommon,
    guest: nlGuest,
    dashboard: nlDashboard,
    amenities: nlAmenities,
  },
}

/**
 * Pad van een catalogusbestand, relatief aan de wortel van dit pakket.
 *
 * Bewust een gewone string en geen filesystem-pad: dit bestand wordt ook in de
 * browser gebundeld en mag daarom niets uit `node:path` of `node:url` gebruiken.
 * Node-scripts lossen het pad zelf op tegen de pakketwortel.
 */
export function getCatalogPath(locale: Locale, namespace: Namespace): string {
  return `locales/${locale}/${namespace}.json`
}

/** Catalogi die de structuurcontrole al doorstaan hebben. */
const validatedCatalogs = new WeakSet<object>()

/** Geeft aan of er voor deze combinatie een catalogus bestaat. */
export function hasCatalog(locale: Locale, namespace: Namespace): boolean {
  return CATALOGS[locale][namespace] !== undefined
}

/**
 * Laadt één catalogus en valideert de structuur.
 *
 * Werpt wanneer de combinatie niet bestaat — er is geen stille terugval op de
 * brontaal, want een halfvertaalde pagina valt niemand op (§17.8).
 */
export function loadCatalog(locale: Locale, namespace: Namespace): Catalog {
  const catalog = CATALOGS[locale][namespace]
  if (catalog === undefined) {
    throw new Error(
      `Er is geen catalogus "${namespace}" voor locale "${locale}" (verwacht bestand: ${getCatalogPath(locale, namespace)})`,
    )
  }
  // Eén keer valideren per catalogus: de inhoud is statisch, dus een tweede
  // controle bij iedere render zou alleen werk kosten.
  if (!validatedCatalogs.has(catalog)) {
    assertCatalog(catalog, `${locale}/${namespace}`)
    validatedCatalogs.add(catalog)
  }
  return catalog
}

/**
 * Laadt meerdere namespaces voor één locale en levert ze als één berichtenobject
 * met de namespace als bovenste sleutel, klaar voor `createTranslator`.
 */
export function loadMessages(locale: Locale, namespaces: readonly Namespace[]): Messages {
  const messages: Record<string, Catalog> = {}
  for (const namespace of namespaces) {
    messages[namespace] = loadCatalog(locale, namespace)
  }
  return messages
}

/**
 * Slaat een geneste structuur plat naar puntsleutels.
 *
 * - geneste objecten worden samengevoegd met een punt: `{ a: { b: 'x' } }` → `a.b`
 * - arrays krijgen hun index als segment: `{ a: ['x'] }` → `a.0`
 * - lege objecten en lege arrays leveren geen sleutel op
 * - `null` en `undefined` worden overgeslagen; die dragen geen bericht
 * - getallen en booleans worden als string overgenomen
 */
export function flattenKeys(input: unknown, prefix = ''): FlatMessages {
  const result: FlatMessages = {}
  collectKeys(input, prefix, result)
  return result
}

function collectKeys(input: unknown, prefix: string, target: FlatMessages): void {
  if (input === null || input === undefined) {
    return
  }
  if (typeof input === 'string') {
    target[prefix] = input
    return
  }
  if (typeof input === 'number' || typeof input === 'boolean') {
    target[prefix] = String(input)
    return
  }
  if (Array.isArray(input)) {
    input.forEach((item, index) => {
      collectKeys(item, join(prefix, String(index)), target)
    })
    return
  }
  if (typeof input === 'object') {
    for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
      collectKeys(value, join(prefix, key), target)
    }
    return
  }
  throw new TypeError(`Niet-ondersteunde waarde op sleutel "${prefix}": ${typeof input}`)
}

function join(prefix: string, segment: string): string {
  return prefix === '' ? segment : `${prefix}.${segment}`
}

/**
 * Controleert dat een catalogus uitsluitend uit geneste objecten met
 * stringbladeren bestaat. Een getal of `null` in een catalogus is een fout: ICU
 * verwacht een berichtpatroon.
 */
export function assertCatalog(value: unknown, label: string, path = ''): asserts value is Catalog {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TypeError(`Catalogus ${label} is geen object${path === '' ? '' : ` op "${path}"`}`)
  }
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const childPath = join(path, key)
    if (typeof child === 'string') {
      continue
    }
    if (typeof child === 'object' && child !== null && !Array.isArray(child)) {
      assertCatalog(child, label, childPath)
      continue
    }
    throw new TypeError(
      `Catalogus ${label} heeft op sleutel "${childPath}" een ${child === null ? 'null' : typeof child} in plaats van een ICU-bericht`,
    )
  }
}
